import React, { useEffect, useState } from "react";
import { biGet, errText } from "./biApi";
import { Tabs, Loading } from "./BiUi";

const TONE = {
  Exemplary: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Needs Improvement": "bg-amber-50 text-amber-700 border-amber-200",
  "Action Required": "bg-rose-50 text-rose-700 border-rose-200",
  Holiday: "bg-slate-100 text-slate-500 border-slate-200"
};
const ICON = { Exemplary: "🟢", "Needs Improvement": "🟡", "Action Required": "🔴", Holiday: "🏖️" };

const Bar = ({ label, value, detail }) => (
  <div>
    <div className="mb-1 flex justify-between text-xs font-bold text-slate-600"><span>{label}</span><span>{value}%{detail ? ` • ${detail}` : ""}</span></div>
    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${value >= 90 ? "bg-emerald-500" : value >= 70 ? "bg-amber-400" : "bg-rose-500"}`} style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  </div>
);

const Feedback = ({ ok, children }) => (
  <div className={`rounded-xl border px-3 py-2 text-xs font-bold ${ok === true ? "border-emerald-200 bg-emerald-50 text-emerald-700" : ok === false ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{children}</div>
);

/** Teacher self-audit: own attendance %, diary %, punctuality for Today / This Week / This Month. */
export default function MyComplianceCard({ user }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("today");
  const [err, setErr] = useState("");
  const classId = user?.class || user?.assignedClass || "";

  useEffect(() => {
    if (!classId) return;
    biGet(user, "/api/analytics/my-compliance", { classId }).then(setData).catch((e) => setErr(errText(e)));
  }, [user, classId]);

  if (!classId) return null;
  const r = data && (view === "today" ? data.today : view === "week" ? data.week : data.month);
  const range = data && view !== "today" ? data.ranges[view] : null;

  let diaryMsg = null, attMsg = null;
  if (r && view === "today") {
    if (r.todayHoliday) { attMsg = <Feedback>🏖️ Today is a holiday - nothing is due.</Feedback>; }
    else {
      attMsg = r.attendanceStatus === "Pending"
        ? <Feedback ok={false}>⏳ Attendance is pending - please submit it now.</Feedback>
        : <Feedback ok={r.punctuality === "On-Time"}>{r.punctuality === "On-Time" ? `✅ Attendance sent on time at ${r.attendanceTime}` : `⚠️ Attendance was sent late at ${r.attendanceTime}`}</Feedback>;
      diaryMsg = r.diaryStatus === "Pending"
        ? <Feedback ok={false}>⏳ Diary is pending - flagged to the Principal until submitted.</Feedback>
        : <Feedback ok={true}>✅ Diary sent{r.diaryTime ? ` at ${r.diaryTime}` : ""}</Feedback>;
    }
  } else if (r) {
    const missedDiary = r.workingDays - r.diaryDays, lateDays = r.lateDays;
    diaryMsg = <Feedback ok={missedDiary === 0 && lateDays === 0 ? true : missedDiary > 0 ? false : undefined}>
      {missedDiary === 0 && lateDays === 0 ? "✅ All diaries and attendance were submitted on time." : `${missedDiary > 0 ? `${missedDiary} diary day(s) pending/missed. ` : ""}${lateDays > 0 ? `${lateDays} late attendance submission(s).` : ""}`}
    </Feedback>;
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" style={{ gridColumn: "1 / -1", marginBottom: 16 }}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-black text-slate-900">📋 My Compliance Card</h3>
          <p className="text-xs font-medium text-slate-500">Class {classId} • visible to you and the Principal{range ? ` • ${range.from} to ${range.to}` : ""}</p>
        </div>
        <Tabs value={view} onChange={setView} options={[["today", "Today"], ["week", "This Week"], ["month", "This Month"]]} />
      </div>
      {err && <Feedback ok={false}>{err}</Feedback>}
      {!data && !err && <Loading />}
      {r && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className={`rounded-full border px-3 py-1 text-xs font-black ${TONE[r.status]}`}>{ICON[r.status]} {r.status}</span>
            <span className={`rounded-full border px-3 py-1 text-xs font-black ${r.punctuality === "Late" || r.lateDays > 0 ? TONE["Needs Improvement"] : TONE.Exemplary}`}>
              ⏱ Punctuality: {view === "today" ? r.punctuality : `${r.punctualityRate}%`}
            </span>
          </div>
          {!r.holiday && (
            <div className="grid gap-3 md:grid-cols-3">
              <Bar label="Attendance completed" value={r.attendanceRate} detail={`${r.attendanceDays}/${r.workingDays}`} />
              <Bar label="Diary submitted" value={r.diaryRate} detail={`${r.diaryDays}/${r.workingDays}`} />
              <Bar label="On-time submissions" value={r.punctualityRate} />
            </div>
          )}
          <div className="grid gap-2 md:grid-cols-2">{attMsg}{diaryMsg}</div>
        </div>
      )}
    </div>
  );
}
